/**
 * Shared legal / policy constants.
 *
 * Single source of truth for the marketing website (legal pages, footer,
 * consent banner), the admin-dashboard (invite/signup acceptance), and the
 * panel-api backend (the policy-acceptance ledger). Changing a version here
 * updates every surface and makes the next acceptance record the new version.
 */

/** The three policy documents users can accept. */
export const POLICY_TYPES = ['TERMS', 'PRIVACY', 'COOKIES'] as const;
export type PolicyType = (typeof POLICY_TYPES)[number];

/** Public marketing-site origin that hosts the policy pages. */
export const SITE_ORIGIN = 'https://www.revvly.ai';

/** Relative routes for the policy pages (used by the website router/links). */
export const LEGAL_ROUTES = {
  terms: '/legal/terms',
  privacy: '/legal/privacy',
  cookies: '/legal/cookies',
  subprocessors: '/legal/sub-processors',
} as const;

/** Absolute policy URLs — for surfaces off the marketing site (the dashboard). */
export const LEGAL_URLS = {
  terms: `${SITE_ORIGIN}${LEGAL_ROUTES.terms}`,
  privacy: `${SITE_ORIGIN}${LEGAL_ROUTES.privacy}`,
  cookies: `${SITE_ORIGIN}${LEGAL_ROUTES.cookies}`,
  subprocessors: `${SITE_ORIGIN}${LEGAL_ROUTES.subprocessors}`,
} as const;

/**
 * Current published version of each policy. Versions are date-based (ISO
 * `YYYY-MM-DD`); `effectiveDate` is the human-readable form shown on the page.
 * Bump `version` whenever a policy materially changes — acceptances recorded
 * afterwards carry the new version, and consent given against an older version
 * can be treated as stale.
 */
export const POLICY_VERSIONS: Record<
  PolicyType,
  { version: string; effectiveDate: string }
> = {
  TERMS: { version: '2026-07-24', effectiveDate: '24 July 2026' },
  PRIVACY: { version: '2026-07-24', effectiveDate: '24 July 2026' },
  COOKIES: { version: '2026-07-24', effectiveDate: '24 July 2026' },
};
